import React, { useState } from 'react';
import { AlertTriangle, X, Copy, CheckCircle } from 'lucide-react';

const ErrorAlert = ({ 
  error, 
  onDismiss, 
  title = "Error", 
  suggestions = [],
  allowCopy = true,
  variant = "error" 
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!allowCopy || !error) return;
    
    try {
      await navigator.clipboard?.writeText(error);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Failed to copy error message');
    }
  };

  if (!error) return null;

  const variantStyles = {
    error: {
      container: 'bg-red-50 border-red-200',
      icon: 'text-red-400',
      title: 'text-red-900',
      text: 'text-red-700',
      button: 'text-red-600 hover:text-red-800'
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200',
      icon: 'text-yellow-400',
      title: 'text-yellow-900',
      text: 'text-yellow-700',
      button: 'text-yellow-600 hover:text-yellow-800'
    }
  };

  const styles = variantStyles?.[variant] || variantStyles?.error;

  return (
    <div className={`border rounded-lg p-4 ${styles?.container}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className={`h-5 w-5 ${styles?.icon}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${styles?.title}`}>
            {title}
          </h3>
          <div className={`mt-1 text-sm ${styles?.text}`}>
            <p>{error}</p>
            
            {suggestions?.length > 0 && (
              <div className="mt-2">
                <p className="font-medium">Suggestions:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {suggestions?.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {allowCopy && (
            <div className="mt-2">
              <button
                onClick={handleCopy}
                className={`inline-flex items-center text-xs ${styles?.button} hover:underline`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Error Message
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        {onDismiss && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onDismiss}
              className={`inline-flex ${styles?.button}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorAlert;